import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, computed, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FileDocument } from '@lib/models/file-document.model';
import {
  Consultant,
  ConsultantOrdreSignatureEnum,
  DerStateTransitionTrigger,
  DocumentSignatureInput,
  EtatDer,
  EtatQpi,
  EtatRecueil,
  GedDocumentType,
  GenerationRecueilTypeEnum,
  Investisseur,
  PersonnePhysique,
  QpiStateTransitionTrigger,
  RecueilStateTransitionTrigger,
  SignataireType,
  UpdateDocumentMetadatasCommandInput,
} from '@lib/models/generated/graphql';
import { AuthService } from '@lib/services/auth-service.service';
import { DocumentsService } from '@lib/services/documents.service';
import { SignatureService } from '@lib/services/signature.service';
import { OuiValidationError } from 'omnium-ui/form-field';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { map } from 'rxjs';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { RecueilService } from '@lib/services/recueil.service';
import { requiredEmailErrors, requiredPhoneErrors } from '@lib/utils/errors-messages';
import { emailValidator } from '@lib/validators/email.validator';
import { frenchPhoneNumberValidator } from '@lib/validators/phone-french.validator';
import { DocumentForm, SelectedSignataire, SignataireForm, SignatureForm } from '../models/signature.form.model';
import { AddSignataireComponent } from './add-signataire/add-signataire.component';
import { MenuNode } from 'omnium-ui/shared';
import { AddDocumentSignatureComponent } from './add-document-signature/add-document-signature.component';
import { AddDocumentTypeEnum } from './add-document-type.enum';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTable } from '@angular/material/table';
import { SignatureRulesService } from './signature-rules.service';
import { RequestErrorPageComponent } from '@lib/components/error-pages/request-error-page/request-error-page.component';
import { StatusTagComponent } from '@lib/components/status-tag/status-tag.component';
import { StepStatusEnum } from '@lib/enums/step-status.enum';
import { getRawFileId } from '@lib/utils/app-utils';
import { ConsultantPreferencesService } from '@lib/services/consultant-preferences.service';

@Component({
  selector: 'app-create-signature-dialog',
  imports: [
    SHARED_IMPORTS,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    AddSignataireComponent,
    AddDocumentSignatureComponent,
    RequestErrorPageComponent,
    StatusTagComponent,
  ],
  templateUrl: './create-signature-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-signature-dialog.component.scss',
})
export class CreateSignatureDialogComponent implements OnInit {
  private readonly signatureRequestErrorMessage =
    "Une erreur est survenue lors du traitement de l'envoi en signature. Veuillez rafraîchir la page et contacter l'administrateur si l'erreur persiste.";

  @ViewChild('addDocumentSignatureDocument', { static: false }) addDocumentSignatureComponent: AddDocumentSignatureComponent;
  @ViewChild(MatTable) table!: MatTable<any>;

  private readonly fb = inject(FormBuilder);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly signatureService = inject(SignatureService);
  private readonly signatureRulesService = inject(SignatureRulesService);
  private readonly investisseurService = inject(InvestisseurService);
  private readonly authService = inject(AuthService);
  private readonly recueilService = inject(RecueilService);
  private readonly documentService = inject(DocumentsService);
  private readonly consultantPreferencesService = inject(ConsultantPreferencesService);

  private readonly environment = inject<any>('environment' as any);

  public readonly dialogRef = inject(MatDialogRef<CreateSignatureDialogComponent>);

  public readonly data = inject<{
    investisseurId: number;
    documents: FileDocument[];
    splitRecueilByInvestisseur?: boolean;
  }>(MAT_DIALOG_DATA);

  public readonly form: FormGroup<SignatureForm> = this.fb.group({
    signataires: this.fb.array<FormGroup<SignataireForm>>([]),
    documents: this.fb.array<FormGroup<DocumentForm>>([]),
  });

  public readonly signataireType = SignataireType;
  public readonly requiredEmailErrors: OuiValidationError[] = requiredEmailErrors;
  public readonly telephoneErrors: OuiValidationError[] = requiredPhoneErrors;
  public readonly displayAddSignataire = signal<boolean>(false);
  public allDocuments: FileDocument[] = this.data?.documents ?? [];
  public readonly documentTableColumns = signal<string[]>([]);
  public readonly loading = signal<boolean>(false);
  public readonly saving = signal<boolean>(false);
  public readonly isTestEnv = ['int', 'dev'].includes(this.environment.env);
  public readonly signatureTestMail = this.environment.signatureTestMail;
  public readonly selectedInvestisseurIds = signal<number[]>([]);
  public readonly investisseur1Id = computed<number>(() => this.investisseurService.investisseurSig()?.id);
  public readonly investisseur2Id = computed<number>(() => this.investisseurService.investisseurConjointSig()?.id);
  public readonly coupleInvestisseurIds = this.investisseurService.coupleInvestisseurIds;
  public readonly displayAddDocument = signal<AddDocumentTypeEnum | undefined>(undefined);
  public readonly documentsToDismiss = signal<string[]>([]);
  readonly isDERSignedOrSigning = toSignal(
    this.recueilService.recueilState$.pipe(
      map(state => state?.derEtat && [EtatDer.Signe, EtatDer.EnSignature, EtatDer.EnSignatureApresSigne].includes(state?.derEtat))
    ),
    {
      initialValue: false,
    }
  );
  readonly isRecueilSignedOrSigning = toSignal(
    this.recueilService.recueilState$.pipe(
      map(
        state =>
          state?.derEtat &&
          [
            EtatRecueil.Signe,
            EtatRecueil.EnSignaturePremierFois,
            EtatRecueil.EnSignatureApresExpiration,
            EtatRecueil.EnSignatureApresSigne,
          ].includes(state?.recueilEtat)
      )
    ),
    {
      initialValue: false,
    }
  );

  readonly isProfilToComplete = toSignal(
    this.recueilService.recueilState$.pipe(map(state => state?.qpiEtat && state?.qpiEtat === EtatQpi.ACompleter)),
    {
      initialValue: false,
    }
  );

  public readonly addDocumentMenuItems: MenuNode[] = [
    { name: 'Depuis mes documents Proxeema', action: () => this.displayAddDocument.set(AddDocumentTypeEnum.FromDocumentMinotaure) },
    {
      name: 'Depuis mon ordinateur',
      action: () => this.displayAddDocument.set(AddDocumentTypeEnum.FromLocalFolder),
    },
  ];
  public readonly globalError = signal<boolean>(false);
  public readonly isFormValid = signal<boolean>(false);
  public readonly generatingKeys = signal<Set<string>>(new Set());
  public readonly consultantId = this.authService.consultant()?.id;
  readonly dragDocumentDisabled = signal<boolean>(false);
  readonly investisseursRelations = signal<Investisseur[]>([]);
  readonly conjointName = computed<string | null>(
    () =>
      `${this.investisseurService.investisseurConjointSig()?.personnePhysique[0].prenom} ${this.investisseurService.investisseurConjointSig()?.personnePhysique[0].nom}`
  );
  readonly errorTooltip = this.signatureRulesService.errorTooltip;

  public StepStatusEnum = StepStatusEnum;
  ngOnInit(): void {
    this.init();
  }

  private async init() {
    this.loading.set(true);

    try {
      // On s'assure d'avoir la dernière version
      await this.investisseurService.getInvestisseur(this.investisseurService.investisseurSig()!.code);

      this.allDocuments = await this.signatureRulesService.getDocuments(this.allDocuments, this.data?.splitRecueilByInvestisseur ?? false);

      this.buildForm();
      this.applyRecueilDefaultActivation();
      this.setDocumentTableColumns();
    } catch (error) {
      console.error(error);
      this.globalError.set(true);
    } finally {
      this.loading.set(false);
      this.getInvestisseursRelations();
    }
  }

  public removeSignataire(index: number): void {
    this.form.controls.signataires.removeAt(index);
    this.afterSignatairesChanged();
  }

  public toggleSignataireActive(event: boolean | null, index: number): void {
    const signataire = this.form.controls.signataires.at(index);
    signataire.controls.order.patchValue(0);
    this.afterSignatairesChanged();
  }

  public dropSignataire(event: CdkDragDrop<FormGroup[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const signataires = this.form.controls.signataires;

    const item = signataires.at(event.previousIndex);
    signataires.removeAt(event.previousIndex);
    signataires.insert(event.currentIndex, item);
    this.updateSignatairesOrder();
    this.setDocumentTableColumns();
    this.table.renderRows();
  }

  public dropDocument(event: CdkDragDrop<FormGroup[]>): void {
    this.dragDocumentDisabled.set(true);
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.documents.controls, event.previousIndex, event.currentIndex);
    this.table.renderRows();
  }

  public addSignataire(investisseur: Investisseur, representantDeNom?: string | null, representantDeIds?: number[] | null): void {
    this.displayAddSignataire.set(false);
    const signataire = this.getSignataireInvestisseurFormGroup(0, investisseur, representantDeNom, representantDeIds);
    this.form.controls.signataires.controls.push(signataire);
    this.afterSignatairesChanged();
  }

  public isGenerating(document: FormGroup<DocumentForm>): boolean {
    const key = document.value.generationKey;
    return !!key && this.generatingKeys().has(key);
  }

  private async ensureDocumentGenerated(document: FormGroup<DocumentForm>): Promise<void> {
    const key = document.value.generationKey;
    if (!document.value.isPendingGeneration || document.value.url || !key) return;

    this.generatingKeys.update(keys => new Set(keys).add(key));
    try {
      const generated = await this.signatureRulesService.generateDocument(key);
      if (generated) {
        document.patchValue({
          url: generated.url ?? null,
          fileId: generated.fileId ?? null,
          filename: generated.fileName,
          isPendingGeneration: false,
        });
      }
    } finally {
      this.generatingKeys.update(keys => {
        const next = new Set(keys);
        next.delete(key);
        return next;
      });
    }
  }

  public async onDocumentNameClick(document: FormGroup<DocumentForm>): Promise<void> {
    await this.ensureDocumentGenerated(document);
    if (document.value.url) {
      window.open(document.value.url, '_blank', 'noopener,noreferrer');
    }
  }

  public toggleDocumentActive(value: boolean | null, document: FormGroup<DocumentForm>): void {
    (document.controls.signataires as FormArray<FormGroup<SelectedSignataire>>).controls.forEach(s => {
      if (value && document.value.disabledInvestisseurId !== s.value.signataireId) {
        s.controls.selected.enable();
      } else {
        s.controls.selected.patchValue(false);
        s.controls.selected.disable();
      }
    });
  }

  private applyRecueilDefaultActivation(): void {
    if (!this.investisseurService.investisseurConjointSig()) return;

    const recueilRows = this.documents.controls.filter(d => d.value.documentType === GedDocumentType.RecInfo && !d.value.isFakeFile);
    const coupleRow = recueilRows.find(d => (d.value.investisseurIds?.length ?? 0) === 2);
    const soloRows = recueilRows.filter(d => (d.value.investisseurIds?.length ?? 0) === 1);

    // No couple/solo redundancy to arbitrate (e.g. only the solo documents were supplied by the caller,
    // as when sending straight from the "Recueil version seul" viewer): leave each row's own default
    // (already computed from its own, variant-aware stepStatus) untouched.
    if (!coupleRow || soloRows.length === 0) return;

    const setActive = (row: FormGroup<DocumentForm>, active: boolean) => {
      row.controls.isActive.patchValue(active);
      this.toggleDocumentActive(active, row);
    };

    const recueilEtat = this.investisseurService.investisseurSig()?.avancementConnaissanceClientInvestisseur?.recueilEtat;
    const preferenceConsultantRecueilType: GenerationRecueilTypeEnum =
      this.consultantPreferencesService.generationRecueilType() ?? GenerationRecueilTypeEnum.Couple;
    const isCoupleActif = preferenceConsultantRecueilType === GenerationRecueilTypeEnum.Couple;

    switch (recueilEtat) {
      case EtatRecueil.ASigner:
      case EtatRecueil.Expire:
        setActive(coupleRow, isCoupleActif);
        soloRows.forEach(row => setActive(row, !isCoupleActif));
        break;
      case EtatRecueil.Signe:
        recueilRows.forEach(row => setActive(row, false));
        break;
    }
  }

  public getSignataires(docIndex: number): FormArray<FormGroup<SelectedSignataire>> {
    return this.form.controls.documents.at(docIndex).get('signataires') as FormArray<FormGroup<SelectedSignataire>>;
  }

  get documents(): FormArray<FormGroup<DocumentForm>> {
    return this.form.get('documents') as FormArray<FormGroup<DocumentForm>>;
  }

  public getSignataireName(signataireId: number | undefined): string {
    const signataire = this.form.controls.signataires.value.find(s => s.id === signataireId);
    return signataire ? `${signataire.firstName} ${signataire.lastName}` : '';
  }

  public toggleAddSignataire(show: boolean): void {
    this.selectedInvestisseurIds.set(
      this.form.controls.signataires.value
        .filter(s => s.signataireType === SignataireType.Investisseur)
        .map(s => s.id as number)
        .filter(Boolean)
    );
    this.displayAddSignataire.set(show);
  }

  public addDocuments(): void {
    const documents = this.addDocumentSignatureComponent.getSelectedDocuments();
    documents.forEach(d => {
      this.form.controls.documents.controls.push(this.getDocumentFormGroup(d, true));
    });
    this.form.controls.documents.updateValueAndValidity({ emitEvent: true });
    this.displayAddDocument.set(undefined);
    this.updateDocumentsToDismiss();
  }

  public removeDocument(index: number): void {
    const documents = this.form.controls.documents;
    documents.removeAt(index);
    this.form.controls.documents.updateValueAndValidity({ emitEvent: true });
    this.updateDocumentsToDismiss();
    this.table.renderRows();
    this.isFormValid.set(this.signatureRulesService.checkFormValid(this.form));
  }

  public trackByControl(_: number, control: AbstractControl) {
    return control;
  }

  public async sendToSignature(): Promise<void> {
    this.loading.set(true);
    try {
      const documents = this.form.controls.documents.controls.filter(
        document => document.value.isActive && document.value.signataires?.some((s: any) => s.selected)
      );

      // Generate only the documents that are actually being sent, on demand
      await Promise.all(documents.filter(d => d.value.isPendingGeneration).map(d => this.ensureDocumentGenerated(d)));

      const missingDocument = documents.find(d => !d.value.url && !d.value.file);
      if (missingDocument) {
        this.snackbarService.open(`Impossible de générer le document ${missingDocument.value.filename}`, 'error');
        return;
      }

      // Save documents in ged
      const oversizedFiles = await this.processDocuments(documents);

      // Display error when oversized files
      if (oversizedFiles.length > 0) {
        this.snackbarService.open(`Les documents suivants dépassent 10 Mo : ${oversizedFiles.join(', ')}`, 'error');
        return;
      }

      // Group documents by signataires list
      const groups = this.groupDocumentsBySignatairesSet(documents);
      // Build inputs
      const inputs = this.buildSignatureInputs(groups);

      // Create signatures
      await this.signatureService.createSignatures(inputs);
      // Update metadata for connaissance client docs
      await this.updateMetadata(documents);
      // Get documents for counts
      this.investisseurService.getInvestisseurDocuments();
      this.dialogRef.close(true);
      this.snackbarService.open('Vos documents ont bien été envoyés en signature', 'success');
    } catch (error) {
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  private async processDocuments(documentsArray: FormGroup<DocumentForm>[]): Promise<string[]> {
    const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

    // Download all files
    const downloads = documentsArray.map(async doc => {
      const docValue = doc.value;
      const file = docValue.url ? await this.documentService.downloadDocumentAsFile(docValue.url) : docValue.file;
      if (!file) {
        throw new Error(`Impossible de récupérer le document ${docValue.filename}`);
      }
      return { doc, file: file as File, docValue };
    });

    const downloaded = await Promise.all(downloads);

    // Check files size
    const oversizedFiles = downloaded.filter(d => d.file.size > MAX_BYTES).map(d => d.docValue.filename ?? '');
    if (oversizedFiles.length > 0) {
      return oversizedFiles;
    }

    // Upload files in GED
    const uploads = downloaded.map(async ({ doc, file, docValue }) => {
      const coupleIds = this.coupleInvestisseurIds();
      const signataireIds = docValue.signataires?.filter((s: any) => s.selected).map((s: any) => s.signataireId) ?? [];

      // Pour le QPI, le document appartient à un investisseur précis (celui dont c'est le profil), quels que soient
      // les signataires sélectionnés (ex: le couple peut co-signer). On ne doit donc pas déduire investisseurIds des
      // signataires pour ce type, sous peine de déclencher la transition d'état QPI pour le mauvais investisseur.
      let investisseurIds: number[];
      if (docValue.documentType === GedDocumentType.ProfInvest) {
        investisseurIds = docValue.investisseurIds ?? [];
      } else {
        investisseurIds = signataireIds.filter(
          (id: number) =>
            this.form.controls.signataires.value.some(s => s.signataireType === SignataireType.Investisseur && s.id === id) &&
            coupleIds.includes(id)
        );

        if (investisseurIds.length === 0) {
          investisseurIds = coupleIds;
        }
      }
      let fileId: string = '';
      if (docValue.fromDocumentMinotaure) {
        fileId = docValue.fileId as string;
        const newMetadata = {
          investisseurIds: investisseurIds.map(id => id.toString()),
        } as UpdateDocumentMetadatasCommandInput;
        await this.documentService.updateFileMetadata(getRawFileId(fileId as string), newMetadata);
      } else {
        fileId = (await this.documentService.createFileAndUpload(
          file,
          docValue.filename!,
          investisseurIds as number[],
          docValue.filename!,
          docValue.documentType!,
          undefined,
          undefined,
          undefined,
          docValue.dateReference ?? undefined
        )) as string;
      }

      doc.patchValue({ fileId });
      doc.patchValue({ investisseurIds });
    });

    await Promise.all(uploads);

    return [];
  }

  private groupDocumentsBySignatairesSet(
    documents: FormGroup<DocumentForm>[]
  ): { signataireIds: number[]; documents: FormGroup<DocumentForm>[] }[] {
    const groups = new Map<string, FormGroup<DocumentForm>[]>();

    documents.forEach(doc => {
      const signatairesArray = doc.controls.signataires;

      const signataireIds = signatairesArray.value
        .filter(s => s.selected && s.signataireId)
        .map(s => s.signataireId as number)
        .sort((a, b) => a - b);

      const key = JSON.stringify(signataireIds);

      if (!groups.has(key)) {
        groups.set(key, []);
      }

      groups.get(key)!.push(doc);
    });

    return Array.from(groups.entries()).map(([key, documents]) => ({
      signataireIds: JSON.parse(key) as number[],
      documents,
    }));
  }

  private buildSignatureInputs(groups: { signataireIds: number[]; documents: FormGroup<DocumentForm>[] }[]): DocumentSignatureInput[] {
    return groups.map(group => {
      const signataireForms = this.form.controls.signataires.controls.filter(
        s => group.signataireIds?.includes(s.value.id as number) && s.value.isActive
      );

      return this.signatureService.buildDocumentSignatureInput(
        group.documents.filter(d => d.value.fileId).map(d => d.value.fileId)! as string[],
        this.signatureService.buildSignatairesInput(signataireForms)
      );
    });
  }

  private async updateMetadata(documents: FormGroup<DocumentForm>[]): Promise<void> {
    const allRequests: Promise<any>[] = [];

    for (const documentForm of documents) {
      const document = documentForm.getRawValue();
      const rawFileId = getRawFileId(document.fileId as string);

      const investisseurIds = document.investisseurIds ?? [];

      let docRequests: Promise<any>[] = [];
      // build requests
      switch (document.documentType) {
        case GedDocumentType.Der:
          docRequests = investisseurIds.map((id: number) =>
            this.investisseurService.updateDERState(
              DerStateTransitionTrigger.ConsultantStartsOrRestartDerSignatureProcess,
              false,
              rawFileId,
              id
            )
          );
          break;

        case GedDocumentType.RecInfo:
          docRequests = investisseurIds.map((id: number) =>
            this.investisseurService.updateRecueilState(
              RecueilStateTransitionTrigger.ConsultantStartsRecueilSignatureProcess,
              rawFileId,
              id
            )
          );
          break;

        case GedDocumentType.ProfInvest:
          docRequests = investisseurIds.map((id: number) =>
            this.investisseurService.updateQPIState(QpiStateTransitionTrigger.ConsultantStartsQpiSignatureProcess, rawFileId, id)
          );
          break;
      }

      allRequests.push(...docRequests);
    }

    if (allRequests.length === 0) {
      return;
    }

    await Promise.all(allRequests);

    await this.investisseurService.refreshRecueilState();
  }

  // Build the form with the consultant and the investisseur(s) in the right order, based on the consultant preferences
  private buildForm(): void {
    const consultant = this.authService.consultant() as Consultant;
    const personne = this.authService.consultantPersonnePhysique() as PersonnePhysique;

    const investisseurs = [this.investisseurService.investisseurSig() as Investisseur];
    if (this.investisseurService.investisseurConjointSig()) {
      investisseurs.push(this.investisseurService.investisseurConjointSig() as Investisseur);
    }

    const ordreSignature = this.consultantPreferencesService.ordreSignature() ?? ConsultantOrdreSignatureEnum.ConsultantEnPremier;
    const isConsultantFirst = ordreSignature === ConsultantOrdreSignatureEnum.ConsultantEnPremier;

    let signatairesFormGroups: FormGroup<SignataireForm>[];

    if (isConsultantFirst) {
      signatairesFormGroups = [
        this.getSignataireConsultantFormGroup(1, consultant, personne),
        ...investisseurs.map((investisseur, index) => this.getSignataireInvestisseurFormGroup(index + 2, investisseur)),
      ];
    } else {
      const investisseurForms = investisseurs.map((investisseur, index) =>
        this.getSignataireInvestisseurFormGroup(index + 1, investisseur)
      );
      signatairesFormGroups = [...investisseurForms, this.getSignataireConsultantFormGroup(investisseurs.length + 1, consultant, personne)];
    }

    this.form.controls.signataires = new FormArray(signatairesFormGroups);
    this.form.controls.documents = new FormArray(this.allDocuments.map(doc => this.getDocumentFormGroup(doc)));
  }

  private getSignataireConsultantFormGroup(order: number, consultant: Consultant, personne: PersonnePhysique): FormGroup<SignataireForm> {
    const telephone = personne.telephone?.find(t => t.estNumeroCorrespondance !== false);
    const email = personne.email?.find(t => t.estAdresseCorrespondance !== false);
    const group = this.fb.group({
      telephone: this.fb.group({
        numero: this.fb.control(telephone?.numero ?? '', {
          nonNullable: true,
          validators: [Validators.required, frenchPhoneNumberValidator],
        }),
        prefix: this.fb.control(telephone?.prefixe ?? null),
        id: this.fb.control(telephone?.id ?? null),
        isActive: this.fb.control(telephone?.estNumeroCorrespondance ?? null),
      }),
      email: this.fb.group({
        adresse: this.fb.control(email?.adresse ?? '', {
          nonNullable: true,
          validators: [Validators.email, Validators.required, emailValidator],
        }),
        id: this.fb.control(email?.id ?? null),
        isActive: this.fb.control(email?.estAdresseCorrespondance ?? null),
      }),
      signataireType: this.fb.control(SignataireType.Consultant, { nonNullable: true }),
      firstName: this.fb.control(personne.prenom, { nonNullable: true }),
      lastName: this.fb.control(personne.nom, { nonNullable: true }),
      id: this.fb.control(consultant.id, { nonNullable: true }),
      isActive: this.fb.control(true, { nonNullable: true }),
      order: this.fb.control(order, { nonNullable: true }),
      representantDeNom: this.fb.control<string | null>(null),
      representantDeIds: this.fb.control<number[] | null>(null),
      derEtat: this.fb.control<EtatDer | null>(null),
      recueilEtat: this.fb.control<EtatRecueil | null>(null),
      recueilFormComplete: this.fb.control<boolean | null>(null),
      qpiEtat: this.fb.control<EtatQpi | null>(null),
    });

    this.addFormGroupValueChangeListeners(group);
    return group;
  }

  private getSignataireInvestisseurFormGroup(
    order: number,
    investisseur: Investisseur,
    representantDeNom?: string | null,
    representantDeIds?: number[] | null
  ): FormGroup<SignataireForm> {
    const personne = investisseur.personnePhysique[0];
    const telephone = personne.telephone?.find(t => t.estNumeroCorrespondance !== false) ?? personne.telephone?.[0];
    const email = personne.email?.find(t => t.estAdresseCorrespondance !== false) ?? personne.email?.[0];
    const group = this.fb.group({
      telephone: this.fb.group({
        numero: this.fb.control(telephone?.numero ?? '', {
          nonNullable: true,
          validators: [Validators.required, frenchPhoneNumberValidator],
        }),
        prefix: this.fb.control(telephone?.prefixe ?? null),
        id: this.fb.control(telephone?.id ?? null),
        isActive: this.fb.control(telephone?.estNumeroCorrespondance ?? null),
      }),
      email: this.fb.group({
        adresse: this.fb.control(email?.adresse ?? '', {
          nonNullable: true,
          validators: [Validators.email, Validators.required, emailValidator],
        }),
        id: this.fb.control(email?.id ?? null),
        isActive: this.fb.control(email?.estAdresseCorrespondance ?? null),
      }),
      signataireType: this.fb.control(SignataireType.Investisseur, { nonNullable: true }),
      firstName: this.fb.control(personne.prenom, { nonNullable: true }),
      lastName: this.fb.control(personne.nom, { nonNullable: true }),
      id: this.fb.control(investisseur.id, { nonNullable: true }),
      isActive: this.fb.control(true, { nonNullable: true }),
      order: this.fb.control(order, { nonNullable: true }),
      representantDeNom: this.fb.control(representantDeNom ?? null),
      representantDeIds: this.fb.control(representantDeIds ?? null),
      derEtat: this.fb.control(investisseur.avancementConnaissanceClientInvestisseur?.derEtat ?? null),
      recueilEtat: this.fb.control(investisseur.avancementConnaissanceClientInvestisseur?.recueilEtat ?? null),
      recueilFormComplete: this.fb.control(investisseur.avancementConnaissanceClientInvestisseur?.isRecueilFormCompleted ?? null),
      qpiEtat: this.fb.control(investisseur.avancementConnaissanceClientInvestisseur?.qpiEtat ?? null),
    });

    this.addFormGroupValueChangeListeners(group);
    return group;
  }

  private getDocumentFormGroup(document: FileDocument, canDelete: boolean = false): FormGroup<DocumentForm> {
    const isActive =
      !document.isFakeFile &&
      (!document.stepStatus ||
        [StepStatusEnum.ToSign, StepStatusEnum.Expired].includes(document.stepStatus as StepStatusEnum) ||
        (document.stepStatus === StepStatusEnum.Signed && !document.disabledIfSigned));
    const group = this.fb.group({
      filename: this.fb.control(document.fileName, { nonNullable: true }),
      description: this.fb.control(document.description ?? ''),
      url: this.fb.control(document.url ?? null),
      documentType: this.fb.control(document.documentType as GedDocumentType),
      isActive: this.fb.control(isActive, { nonNullable: true }),
      file: this.fb.control<File | null>(document.file ?? null),
      canDelete: this.fb.control<boolean>(canDelete, { nonNullable: true }),
      fileId: this.fb.control<string | null>(document.fileId ?? null),
      isFakeFile: this.fb.control(document.isFakeFile ?? false, { nonNullable: true }),
      isPendingGeneration: this.fb.control(document.isPendingGeneration ?? false, { nonNullable: true }),
      generationKey: this.fb.control<string | null>(document.generationKey ?? null),
      tooltip: this.fb.control(this.getTooltip(document), { nonNullable: true }),
      investisseurIds: this.fb.control<number[]>(document.investisseurIds ?? this.coupleInvestisseurIds(), { nonNullable: true }),
      fromDocumentMinotaure: this.fb.control(document.fromDocumentMinotaure ?? false, { nonNullable: true }),
      stepStatus: this.fb.control(document.stepStatus ?? null),
      dateReference: this.fb.control(document.dateReference ?? null),
      disabledInvestisseurId: this.fb.control(document.disabledInvestisseurId ?? null),
      signataires: this.fb.array<FormGroup<SelectedSignataire>>(
        (this.form.controls.signataires.value.filter((s: any) => s.isActive) ?? []).map((signataire: any) =>
          this.fb.group({
            signataireId: this.fb.control(signataire.id as number, { nonNullable: true }),
            selected: this.fb.control(
              {
                value: false,
                disabled: !isActive || (!!document.disabledInvestisseurId && document.disabledInvestisseurId === signataire.id),
              },
              { nonNullable: true }
            ),
          })
        )
      ),
    });

    this.addFormGroupValueChangeListeners(group);
    return group;
  }

  private getTooltip(document: FileDocument): string | null {
    if (!document.isFakeFile) {
      return null;
    }
    if (
      !this.isDERSignedOrSigning() &&
      document.documentType !== GedDocumentType.Der &&
      !this.allDocuments.some(d => d.documentType === GedDocumentType.Der && !d.isFakeFile)
    ) {
      return `L'envoi du ${document.description} en signature nécessite que le DER soit préalablement complété`;
    }
    if (
      !this.isRecueilSignedOrSigning() &&
      !this.isProfilToComplete() &&
      document.documentType === GedDocumentType.ProfInvest &&
      !this.allDocuments.some(d => d.documentType === GedDocumentType.ProfInvest && !d.isFakeFile)
    ) {
      return `L'envoi du profil investisseur en signature nécessite que le recueil d'informations soit préalablement complété`;
    } else {
      return `Tous les champs obligatoires du ${document.description} doivent être renseignés pour que le document puisse être envoyé en signature`;
    }
  }

  private addFormGroupValueChangeListeners(formGroup: FormGroup): void {
    formGroup.valueChanges.subscribe(() => {
      this.isFormValid.set(this.signatureRulesService.checkFormValid(this.form));
    });
  }

  private afterSignatairesChanged(): void {
    this.updateSignatairesOrder();
    this.updateDocumentsSignataires();
    this.setDocumentTableColumns();
  }

  private updateSignatairesOrder(): void {
    this.form.controls.signataires.controls
      .filter((s: FormGroup<SignataireForm>) => s.value.isActive)
      .forEach((s: FormGroup<SignataireForm>, index: number) => {
        s.controls.order.patchValue(index + 1);
        s.controls.email.controls.adresse.updateValueAndValidity();
        s.controls.telephone.controls.numero.updateValueAndValidity();
      });

    const globalOrder = this.form.controls.signataires.value.map(s => s.id) as number[];

    this.form.controls.documents.controls.forEach((doc: FormGroup<DocumentForm>) => {
      const signatairesArray = doc.get('signataires') as FormArray<FormGroup<SelectedSignataire>>;

      const sorted = this.sortByGlobalOrder(signatairesArray.value, globalOrder);
      signatairesArray.clear();

      sorted.forEach(s => {
        signatairesArray.push(
          this.fb.group({
            signataireId: this.fb.control(s.signataireId as number, { nonNullable: true }),
            selected: this.fb.control(
              {
                value: s.selected,
                disabled:
                  !doc.value.isActive || (!!doc.value.disabledInvestisseurId && doc.value.disabledInvestisseurId === s.signataireId),
              },
              { nonNullable: true }
            ),
          })
        );
      });
    });
  }

  private sortByGlobalOrder(signataires: any[], order: number[]): any[] {
    return signataires.sort((a, b) => {
      return order.indexOf(a.signataireId) - order.indexOf(b.signataireId);
    });
  }

  private updateDocumentsSignataires(): void {
    const signataireIds = this.form.controls.signataires.value.filter((s: any) => s.isActive).map((s: any) => s.id);

    this.form.controls.documents.controls.forEach((doc: FormGroup<DocumentForm>) => {
      const signatairesArray = doc.get('signataires') as FormArray<FormGroup<SelectedSignataire>>;

      for (let i = signatairesArray.length - 1; i >= 0; i--) {
        const s = signatairesArray.at(i).value;
        if (!signataireIds.includes(s.signataireId as number)) {
          signatairesArray.removeAt(i);
        }
      }

      signataireIds.forEach((id: number) => {
        const exists = signatairesArray.value.some((s: any) => s.signataireId === id);
        if (!exists) {
          signatairesArray.push(
            this.fb.group({
              signataireId: this.fb.control(id as number, { nonNullable: true }),
              selected: this.fb.control(
                {
                  value: false,
                  disabled: !doc.value.isActive || (!!doc.value.disabledInvestisseurId && doc.value.disabledInvestisseurId === id),
                },
                { nonNullable: true }
              ),
            })
          );
        }
      });
    });
  }

  private setDocumentTableColumns(): void {
    const cols = ['dragHandle', 'status', 'document'];
    if (this.form.controls.signataires.length > 0) {
      this.form.controls.signataires.value
        .filter((s: any) => s.isActive)
        .forEach((signataire: any) => {
          cols.push('signataire-' + signataire.id);
        });
    }
    cols.push('option');
    this.documentTableColumns.set(cols);
  }

  private updateDocumentsToDismiss(): void {
    this.documentsToDismiss.set(
      this.form.controls.documents.value
        .map((v: any) => v.fileId)
        .filter((fileId: any): fileId is string => fileId !== null && fileId !== undefined)
    );
  }

  private async getInvestisseursRelations(): Promise<void> {
    // Get relations famille et autre du couple de la fiche
    const data = await this.investisseurService.getInvestisseursRelations();
    const relations = [
      ...(data?.investisseur?.personnePhysique?.[0]?.relationFamillePersonnePhysique?.map(rel => rel.autrePersonnePhysique) ?? []),
      ...(data?.coInvestisseur?.personnePhysique?.[0]?.relationFamillePersonnePhysique?.map(rel => rel.autrePersonnePhysique) ?? []),
      ...(data?.investisseur?.personnePhysique?.[0]?.relationAutrePersonnePhysique?.map(rel => rel.autrePersonnePhysique) ?? []),
      ...(data?.coInvestisseur?.personnePhysique?.[0]?.relationAutrePersonnePhysique?.map(rel => rel.autrePersonnePhysique) ?? []),
    ];
    if (this.investisseurService.investisseurConjointSig()) {
      relations.push(
        this.investisseurService.investisseurSig()?.personnePhysique[0].relationCouplePersonnePhysique
          ?.autrePersonnePhysique as PersonnePhysique
      );
    }

    const investisseursRelations = Array.from(
      new Map(
        relations
          .filter(
            (personne): personne is PersonnePhysique =>
              personne !== null && personne !== undefined && personne.etatCivil?.typeSituationEtatCivilCode !== 'DECEDE'
          )
          .map(personne => {
            const investisseur = {
              id: personne.investisseur?.[0]?.id,
              statutCode: personne.investisseur?.[0]?.statutCode,
              personnePhysique: [personne],
            } as Investisseur;

            return [investisseur.id, investisseur] as const;
          })
          .filter(([id]) => id != null)
      ).values()
    );

    this.investisseursRelations.set(investisseursRelations);

    // Check des représentants légaux et ajout en signataire
    [
      {
        source: data?.investisseur,
        getName: () => this.investisseurService.investisseurNameSig(),
        getId: () => this.investisseur1Id(),
      },
      {
        source: data?.coInvestisseur,
        getName: () => this.conjointName(),
        getId: () => this.investisseur2Id(),
      },
    ].forEach(({ source, getName, getId }) => {
      const representants =
        source?.personnePhysique?.[0]?.relationAutrePersonnePhysique
          ?.filter(r => r.typeRelationAutreCode === 'REPRESENT_LEGAL_REV')
          ?.map(r => r.autrePersonnePhysique) ?? [];

      let parents =
        source?.personnePhysique?.[0]?.relationFamillePersonnePhysique
          ?.filter(r => r.typeRelationFamilleCode === 'PARENT_REV')
          ?.map(r => r.autrePersonnePhysique) ?? [];

      const parentPersonnePhysiqueIds = parents.map(p => p?.id);
      const parentsRepresentants =
        (source?.personnePhysique?.[0]?.relationFamillePersonnePhysique1 ?? [])
          .concat(source?.personnePhysique?.[0]?.relationFamillePersonnePhysique2 ?? [])
          .filter(
            r =>
              r.typeRelationFamilleCode === 'PARENT' &&
              r.autoriteParentale === true &&
              (parentPersonnePhysiqueIds.includes(r.personnePhysique1.id) || parentPersonnePhysiqueIds.includes(r.personnePhysique2.id))
          )
          ?.map(r => (r.personnePhysique1.id === source?.personnePhysique?.[0]?.id ? r.personnePhysique2.id : r.personnePhysique1.id)) ??
        [];

      parents = parents.filter(p => parentsRepresentants.includes(p?.id));

      const relations = representants.concat(parents);

      if (!relations || relations.length === 0) return;

      const personne = this.investisseurService.investisseurSig()?.personnePhysique?.[0];

      if (personne?.dateNaissance && this.isMinor(personne.dateNaissance)) {
        relations
          .filter((pp, index, self) => index === self.findIndex(s => s?.id === pp?.id))
          .forEach(pp => {
            const id = pp?.investisseur?.[0]?.id;
            if (!id) return;

            const investisseur = investisseursRelations.find(inv => inv.id === id);
            if (!investisseur) return;

            const existingSignataire = this.form.controls.signataires.controls.find(
              (s: FormGroup<SignataireForm>) => s.value.signataireType === SignataireType.Investisseur && s.value.id === id
            );
            if (existingSignataire) {
              existingSignataire.controls.representantDeNom.patchValue(existingSignataire.value.representantDeNom + ', ' + getName());
              existingSignataire.controls.representantDeIds.patchValue([...(existingSignataire.value.representantDeIds || []), getId()]);
              return;
            }

            this.addSignataire(investisseur, getName(), [getId()]);
          });
      }
    });
  }

  private isMinor(dateNaissance: string): boolean {
    const birth = new Date(dateNaissance);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();

    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) {
      age--;
    }

    return age < 18;
  }

  public toggleAllSignataires(checkAll: boolean = true): void {
    this.form.controls.documents.controls.forEach(doc => {
      // On ignore les documents inactifs (bien que leurs signataires soient sûrement disabled)
      if (!doc.value.isActive) return;

      doc.controls.signataires.controls.forEach(signataire => {
        const selectedControl = signataire.controls.selected;

        if (selectedControl.enabled) {
          selectedControl.patchValue(checkAll);
        }
      });
    });
  }
}
